import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Invoice } from '../../types/invoiceTypes';
import { formatCurrency, formatDateTime, drawStyledCompanyName } from '../../utils/helpers';
import jsPDF from 'jspdf';
import Button from '../../components/common/Button/Button';
import styles from './InvoiceDetail.module.scss';
import invoiceService from '../../services/invoiceService';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import settingsService from '../../services/settingsService';
import qrCodeImage from '../../assets/images/phonepe_qr_code.png';
import { useAuth } from '../../hooks/useAuth';

const InvoiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { getCurrentCompanyId } = useAuth();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({ name: '', address: '', phone: '', email: '' });
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadInvoice = async () => {
      if (!id) return;
      
      setLoading(true);
      try {
        const invoice = await invoiceService.getInvoice(id);
        setInvoice(invoice);
      } catch (error) {
        console.error('Failed to load invoice:', error);
        toast.error('Failed to load invoice details');
        setInvoice(null);
      } finally {
        setLoading(false);
      }
    };

    loadInvoice();
  }, [id]);

  useEffect(() => {
    settingsService.getCompanyInfo().then(data => {
      setCompanyInfo({
        name: data.name || '',
        address: data.address || '',
        phone: data.phone || '',
        email: data.email || '',
      });
    }).catch(error => {
      console.error('Failed to load company info:', error);
    });
  }, []);

  const generatePDF = async (download: boolean = true) => {
    if (!invoice) {
      toast.error('Unable to generate PDF: Invoice data not available');
      return;
    }

    setIsGeneratingPDF(true);

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - (margin * 2);
      let yPosition = margin;

      // Helper function to check if we need a new page
      const checkNewPage = (requiredSpace: number) => {
        if (yPosition + requiredSpace > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // Set default font
      pdf.setFont('helvetica', 'normal');

      // Company Header Section with styled name
      if (companyInfo.name || companyInfo.address || companyInfo.phone || companyInfo.email) {
        // Company header box
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.5);
        const headerHeight = 30;
        pdf.rect(margin, yPosition, contentWidth, headerHeight);
        
        yPosition += 13;
        
        if (companyInfo.name) {
          drawStyledCompanyName(pdf, companyInfo.name, yPosition, pageWidth);
          yPosition += 8;
        }
        
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        
        if (companyInfo.address) {
          pdf.text(companyInfo.address, pageWidth / 2, yPosition, { align: 'center' });
          yPosition += 5;
        }
        
        const contactInfo = [];
        if (companyInfo.phone) contactInfo.push(`Phone: ${companyInfo.phone}`);
        if (companyInfo.email) contactInfo.push(`Email: ${companyInfo.email}`);
        
        if (contactInfo.length > 0) {
          pdf.text(contactInfo.join(' | '), pageWidth / 2, yPosition, { align: 'center' });
        }
        
        yPosition = margin + headerHeight + 15;
      }

      // Invoice Header Section
      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(0.3);
      
      // Invoice title and number
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 102, 204); // Blue color
      pdf.text('INVOICE', margin, yPosition);
      
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text(`#${invoice.invoice_number}`, pageWidth - margin, yPosition, { align: 'right' });
      
      yPosition += 10;
      
      // Date and Status
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Date: ${formatDateTime(invoice.created_at)}`, margin, yPosition);
      
      const statusText = `Status: ${invoice.status.replace('_', ' ').toUpperCase()}`;
      pdf.text(statusText, pageWidth - margin, yPosition, { align: 'right' });
      
      yPosition += 15;

      // Bill To Section
      checkNewPage(50);
      
      // Bill To box
      const billToHeight = 35;
      pdf.setDrawColor(200, 200, 200);
      pdf.setFillColor(248, 248, 248);
      pdf.rect(margin, yPosition, contentWidth, billToHeight, 'FD');
      
      yPosition += 8;
      
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('BILL TO:', margin + 5, yPosition);
      yPosition += 8;
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text(invoice.customer_name, margin + 5, yPosition);
      yPosition += 5;
      
      pdf.setFont('helvetica', 'normal');
      if (invoice.customer_email) {
        pdf.text(`Email: ${invoice.customer_email}`, margin + 5, yPosition);
        yPosition += 4;
      }
      
      if (invoice.customer_phone) {
        pdf.text(`Phone: ${invoice.customer_phone}`, margin + 5, yPosition);
        yPosition += 4;
      }
      
      if (invoice.customer_address) {
        const addressLines = pdf.splitTextToSize(`Address: ${invoice.customer_address}`, contentWidth - 10);
        pdf.text(addressLines, margin + 5, yPosition);
      }
      
      yPosition = yPosition + billToHeight - 8;

      // Items Table
      checkNewPage(80);
      
      // Table setup with better proportions
      const tableStartY = yPosition;
      const rowHeight = 12;
      const headerHeight = 14;
      
      // Better column width distribution for A4 (210mm - 30mm margin = 180mm)
      const colWidths = [15, 70, 35, 30, 35]; // S.No, Description, Amount, S/C, Total
      const colPositions = [margin];
      
      // Calculate column positions
      for (let i = 0; i < colWidths.length - 1; i++) {
        colPositions.push(colPositions[i] + colWidths[i]);
      }

      // Table header background
      pdf.setFillColor(52, 73, 94); // Dark blue
      pdf.setDrawColor(52, 73, 94);
      pdf.rect(margin, yPosition, contentWidth, headerHeight, 'FD');
      
      // Header text
      pdf.setTextColor(255, 255, 255); // White text
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      
      const headers = ['S.No', 'Description', 'Amount', 'S/C', 'Total'];
      
      // S.No - center aligned
      pdf.text(headers[0], colPositions[0] + (colWidths[0] / 2), yPosition + 9, { align: 'center' });
      
      // Description - left aligned
      pdf.text(headers[1], colPositions[1] + 3, yPosition + 9);
      
      // Amount - center aligned
      pdf.text(headers[2], colPositions[2] + (colWidths[2] / 2), yPosition + 9, { align: 'center' });
      
      // S/C - center aligned
      pdf.text(headers[3], colPositions[3] + (colWidths[3] / 2), yPosition + 9, { align: 'center' });
      
      // Total - center aligned
      pdf.text(headers[4], colPositions[4] + (colWidths[4] / 2), yPosition + 9, { align: 'center' });
      
      yPosition += headerHeight;

      // Table rows
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.setDrawColor(200, 200, 200); // Light gray for borders
      pdf.setLineWidth(0.2);
      
      let rowIndex = 0;
      for (const item of invoice.items) {
        checkNewPage(rowHeight + 5);
        
        // Alternating row colors
        if (rowIndex % 2 === 1) {
          pdf.setFillColor(248, 249, 250);
          pdf.rect(margin, yPosition, contentWidth, rowHeight, 'F');
        }
        
        const textY = yPosition + 8;
        
        // S.No - center aligned
        pdf.text((rowIndex + 1).toString(), colPositions[0] + (colWidths[0] / 2), textY, { align: 'center' });
        
        // Description - left aligned with proper wrapping
        const maxDescWidth = colWidths[1] - 6;
        const descLines = pdf.splitTextToSize(item.description, maxDescWidth);
        
        if (descLines.length > 1) {
          // If text wraps, show first line and add "..." if needed
          pdf.text(descLines[0], colPositions[1] + 3, textY);
          if (descLines.length > 1) {
            // You might want to increase row height for wrapped text or truncate
            const truncatedDesc = item.description.length > 30 ? 
              item.description.substring(0, 30) + '...' : item.description;
            pdf.text(truncatedDesc, colPositions[1] + 3, textY);
          }
        } else {
          pdf.text(item.description, colPositions[1] + 3, textY);
        }
        
        // Amount - center aligned within column
        const amountText = formatCurrency(item.amount).replace('₹', ''); // Remove currency symbol as it's in header
        pdf.text(amountText, colPositions[2] + (colWidths[2] / 2), textY, { align: 'center' });
        
        // Service Charge - center aligned within column
        const scText = formatCurrency(item.service_charge || 0).replace('₹', '');
        pdf.text(scText, colPositions[3] + (colWidths[3] / 2), textY, { align: 'center' });
        
        // Total Amount - center aligned within column
        const itemTotal = (Number(item.amount) || 0) + (Number(item.service_charge) || 0);
        const totalText = formatCurrency(itemTotal).replace('₹', '');
        pdf.text(totalText, colPositions[4] + (colWidths[4] / 2), textY, { align: 'center' });
        
        yPosition += rowHeight;
        rowIndex++;
      }

      // Table borders
      pdf.setDrawColor(52, 73, 94); // Dark blue for outer border
      pdf.setLineWidth(0.5);
      
      // Outer border
      pdf.rect(margin, tableStartY, contentWidth, (rowIndex * rowHeight) + headerHeight);
      
      // Header separator line
      pdf.line(margin, tableStartY + headerHeight, margin + contentWidth, tableStartY + headerHeight);
      
      // Vertical column separators
      pdf.setDrawColor(200, 200, 200); // Light gray for internal lines
      pdf.setLineWidth(0.2);
      
      for (let i = 1; i < colPositions.length; i++) {
        pdf.line(colPositions[i], tableStartY, colPositions[i], yPosition);
      }
      
      // Horizontal row separators (lighter)
      let currentRowY = tableStartY + headerHeight;
      for (let i = 0; i < rowIndex; i++) {
        currentRowY += rowHeight;
        if (i < rowIndex - 1) { // Don't draw line after last row
          pdf.line(margin + 1, currentRowY, margin + contentWidth - 1, currentRowY);
        }
      }

      yPosition += 15;

      // Total Section
      checkNewPage(25);
      
      const totalBoxWidth = 80;
      const totalBoxHeight = 15;
      const totalBoxX = pageWidth - margin - totalBoxWidth;
      
      pdf.setFillColor(52, 73, 94);
      pdf.setTextColor(255, 255, 255);
      pdf.rect(totalBoxX, yPosition, totalBoxWidth, totalBoxHeight, 'F');
      
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('TOTAL AMOUNT:', totalBoxX + 2, yPosition + 10);
      pdf.text(invoice.total_amount.toString(), totalBoxX + totalBoxWidth - 10, yPosition + 10, { align: 'right' });
      
      yPosition += totalBoxHeight + 20;

      // Notes Section
      if (invoice.notes) {
        checkNewPage(30);
        
        pdf.setTextColor(0, 0, 0);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('NOTES:', margin, yPosition);
        yPosition += 8;
        
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        const notesLines = pdf.splitTextToSize(invoice.notes, contentWidth);
        pdf.text(notesLines, margin, yPosition);
      }

      // QR Code Section (Scan to Pay) - only for company_id 1
      if (getCurrentCompanyId() === 1) {
        try {
          const qrImg = new Image();
          qrImg.src = qrCodeImage;
          await new Promise<void>((resolve, reject) => {
            qrImg.onload = () => resolve();
            qrImg.onerror = () => reject(new Error('Failed to load QR code image'));
          });

          const qrSize = 35;
          checkNewPage(qrSize + 15);

          yPosition += 5;
          pdf.setTextColor(0, 0, 0);
          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Scan to Pay', pageWidth / 2, yPosition, { align: 'center' });
          yPosition += 6;

          const qrX = pageWidth / 2 - qrSize / 2;
          pdf.addImage(qrImg, 'PNG', qrX, yPosition, qrSize, qrSize);
          yPosition += qrSize + 5;
        } catch (error) {
          console.error('Failed to add QR code to PDF:', error);
        }
      }

      // Footer
      const footerY = pageHeight - 15;
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'italic');
      pdf.setTextColor(128, 128, 128);
      pdf.text(`Generated on ${new Date().toLocaleDateString()}`, pageWidth / 2, footerY, { align: 'center' });

      // Handle download or print
      if (download) {
        const fileName = `Invoice-${invoice.invoice_number}-${new Date().toLocaleDateString().replace(/\//g, '-')}.pdf`;
        pdf.save(fileName);
        toast.success('PDF downloaded successfully!');
      } else {
        pdf.autoPrint();
        const pdfBlob = pdf.output('blob');
        const blobUrl = URL.createObjectURL(pdfBlob);
        const printWindow = window.open(blobUrl);
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print();
            URL.revokeObjectURL(blobUrl);
          };
        }
      }

    } catch (error) {
      console.error('PDF generation failed:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // const handleDownloadPDF = () => {
  //   if (isGeneratingPDF) return;
  //   generatePDF(true);
  // };

  const handlePrint = () => {
    if (isGeneratingPDF) return;
    generatePDF(false);
  };

  if (loading) {
    return (
      <div className={styles.invoiceDetail}>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>Loading invoice details...</p>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className={styles.invoiceDetail}>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>Invoice not found.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={{ 
        display: 'flex', 
        gap: '0.75rem', 
        margin: '2rem auto 1rem auto', 
        maxWidth: 700, 
        justifyContent: 'flex-end' 
      }}>
        <Button 
          variant="secondary" 
          onClick={handlePrint} 
          disabled={isGeneratingPDF}
        >
          <svg 
            width="18" 
            height="18" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            style={{ marginRight: '8px' }}
          >
            <polyline points="6,9 6,2 18,2 18,9"></polyline>
            <rect x="6" y="14" width="12" height="8" rx="2"></rect>
            <path d="M6,18H4a2,2 0 0,1-2-2V11a2,2 0 0,1,2-2H20a2,2 0 0,1,2,2v5a2,2 0 0,1-2,2H18"></path>
          </svg>
          Print Invoice
        </Button>
        {/* <Button 
          variant="primary" 
          onClick={handleDownloadPDF} 
          disabled={isGeneratingPDF} 
          loading={isGeneratingPDF}
        >
          <svg 
            width="18" 
            height="18" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            style={{ marginRight: '8px' }}
          >
            <path d="M21,15v4a2,2 0 0,1-2-2H5a2,2 0 0,1-2-2v-4"></path>
            <polyline points="7,10 12,15 17,10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          {isGeneratingPDF ? 'Generating...' : 'Download PDF'}
        </Button> */}
      </div>

      <div className={styles.invoiceDetail} ref={contentRef}>
        <div className={styles.companyDetails} style={{ marginBottom: '1rem', textAlign: 'center' }}>
          {companyInfo.name && (
            <div style={{ fontWeight: 'bold', fontSize: '2.2rem' }}>
              {companyInfo.name}
            </div>
          )}
          {companyInfo.address && (
            <div style={{ fontSize: '1rem' }}>
              {companyInfo.address}
            </div>
          )}
          {companyInfo.phone && (
            <div style={{ fontSize: '1rem' }}>
              Phone: {companyInfo.phone}
            </div>
          )}
          {companyInfo.email && (
            <div style={{ fontSize: '1rem' }}>
              Email: {companyInfo.email}
            </div>
          )}
        </div>

        <div className={styles.header}>
          <div className={styles.invoiceNumber}>
            Invoice #{invoice.invoice_number}
          </div>
          <div className={styles.meta}>
            <span>
              <strong>Date:</strong> {formatDateTime(invoice.created_at)}
            </span>
            <span className={`${styles.status} ${styles[invoice.status]}`}>
              {invoice.status.replace('_', ' ').toUpperCase()}
            </span>
          </div>
        </div>

        <div className={styles.section}>
          <h3>Bill To</h3>
          <div className={styles.detailsGrid}>
            <div className={styles.detailItem}>
              <label>Customer Name</label>
              <span>{invoice.customer_name}</span>
            </div>
            <div className={styles.detailItem}>
              <label>Customer Email</label>
              <span>{invoice.customer_email || 'N/A'}</span>
            </div>
            <div className={styles.detailItem}>
              <label>Customer Phone</label>
              <span>{invoice.customer_phone || 'N/A'}</span>
            </div>
            <div className={styles.detailItem}>
              <label>Customer Address</label>
              <span>{invoice.customer_address || 'N/A'}</span>
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <h3>Items</h3>
          <table className={styles.itemsTable}>
            <thead>
              <tr>
                <th>S.No</th>
                <th>Description</th>
                <th>Amount</th>
                <th>S/C</th>
                <th>Total amount</th>
                <th>Created By</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item, index) => (
                <tr key={item.id}>
                  <td>{index + 1}</td>
                  <td>{item.description}</td>
                  <td>{formatCurrency(item.amount)}</td>
                  <td>{formatCurrency(item.service_charge || 0)}</td>
                  <td>
                    {formatCurrency(
                      (Number(item.amount) || 0) + (Number(item.service_charge) || 0)
                    )}
                  </td>
                  <td>{item.created_by_name || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={styles.section}>
          <div className={styles.totalRow}>
            <span>Total Amount:</span>
            <span>{formatCurrency(invoice.total_amount)}</span>
          </div>
        </div>

        {invoice.notes && (
          <div className={styles.section}>
            <div className={styles.notes}>
              <strong>Notes:</strong>
              <div>{invoice.notes}</div>
            </div>
          </div>
        )}
      </div>

      {isGeneratingPDF && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          padding: '2rem',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          zIndex: 1000,
          textAlign: 'center',
          color: '#3b82f6'
        }}>
          <div>Generating PDF...</div>
          <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', opacity: 0.7 }}>
            Creating your invoice PDF programmatically
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
};

export default InvoiceDetail;
